<script setup>
import { computed, ref, watchEffect } from "vue";
import { trans } from "i18n";

const props = defineProps({
	aspectRatio: [String, Number],
	height: String,
	width: String,
	label: String,
	tooltip: String,
	disabled: Boolean,
	required: Boolean,
	mimeTypes: {
		type: Array,
		default: () => ["image/*"]
	},
	src: String,
	modelValue: File
});
const emit = defineEmits(["update:modelValue"]);
const input = ref(null);

const imgSrc = computed(() => props.modelValue ? URL.createObjectURL(props.modelValue) : props.src);
const _required = computed(() => props.required && !imgSrc.value);

function drop(event) {
	if (event.dataTransfer.items.length === 0)
		return;

	const item = event.dataTransfer.items[0];

	if (item.kind !== "file")
		return;

	if (!props.mimeTypes.includes(item.type.toLowerCase()))
		return;

	emit("update:modelValue", item.getAsFile());
}

watchEffect(() => {
	let msg = "";

	if (props.modelValue && props.modelValue.size > 8388608)
		msg = trans.gettext("File size must not exceed %1 megabytes", 8);

	if (input.value)
		input.value.setCustomValidity(msg);
});
</script>

<template>
	<label :class="{ disabled, 'fixed-size': (width || height) }" class="vui-img-input">
		<template v-if="label">
			<span class="label">{{ label }}</span>
			<span v-if="!required" class="badge">{{ $gettext("optional") }}</span>
			<i v-if="tooltip || $slots.tooltip" class="fa-solid fa-square-info tooltip" @click.prevent>
				<div class="tooltip-content"><slot name="tooltip">{{ tooltip }}</slot></div>
			</i>
		</template>
		<div
			class="img-container"
			:style="{ width: width, height: height, aspectRatio: aspectRatio }"
			:title="$gettext('Click to select a file or drag an image here')"
			@drop.prevent="drop"
			@dragenter.prevent
			@dragover.prevent
		>
			<template v-if="imgSrc">
				<i class="edit fa-duotone fa-pen-circle"></i>
				<img :src="imgSrc">
			</template>
			<div v-else class="grid gap-8 justify-items-center placeholder">
				<i class="fa-light fa-download align-self-end"></i>
				<div>{{ $gettext("Click to select a file or drag an image here") }}</div>
			</div>
			<input
				ref="input"
				:disabled="disabled"
				:required="_required"
				:accept="mimeTypes.join(',')"
				type="file"
				@change="$emit('update:modelValue', $event.target.files[0])"
			>
		</div>
	</label>
</template>
